/**
 * AmenitiesListCommunity — Weidner Via6 amenities page (community row).
 *
 * One of the two split rows that replaced the merged `AmenitiesList`
 * slot on the amenities page so a `DividerBG` banner can sit between
 * the Community and Interior galleries. See `_AmenitiesListRow.tsx`
 * for the rendering helper and a fuller history note.
 *
 * Editor surface:
 *   - `amenitieslistcommunity.heading` — the row heading (richtext).
 *     Default wraps the leading word in a `<span class="vibe-cursive-accent">`
 *     so the brand cursive treatment applies; editing the heading in
 *     the WYSIWYG will keep the span as long as the user doesn't
 *     delete it.
 *   - `amenitieslistcommunity.items` — community amenity items
 *     (defaults to the `amenities` collection filtered to
 *     `group === 'Community'`, matching the upstream rise filter).
 *   - `amenitieslistcommunity.photos` — community photo gallery
 *     (defaults to `photo_gallery` filtered to `title === 'Community'`).
 *
 * Visual: community row sits above the divider banner with the image
 * panel on the right (consistent with how the legacy merged section
 * was rendered after the Weidner override flipped the rise default).
 */

import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding } from '@vibesites/runtime/react';
import {
  AmenitiesListRow,
  type AmenityItem,
  type GalleryPhoto,
} from './_AmenitiesListRow';

// Strip HTML tags so a richtext heading can be used as plain alt-text
// fallback on the photo slider.
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

const HEADING_DEFAULT_HTML =
  '<p><span class="vibe-cursive-accent">Community</span> amenities</p>';

export function AmenitiesListCommunity() {
  const items = useCollectionBinding<AmenityItem>('amenitieslistcommunity.items', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const photos = useCollectionBinding<GalleryPhoto>('amenitieslistcommunity.photos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  return (
    <AmenitiesListRow
      headingNode={
        <RichTextContent
          path="amenitieslistcommunity.heading"
          label="Heading"
          defaultValue={HEADING_DEFAULT_HTML}
          as="h3"
          className="amenities-col-heading"
        />
      }
      headingText={stripHtml(HEADING_DEFAULT_HTML) || 'Community amenities'}
      items={items}
      photos={photos}
      imagePosition="right"
    />
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesListCommunity',
  displayName: 'Amenities List — Community',
  tokens: {
    'amenitieslistcommunity.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p><span class="vibe-cursive-accent">Community</span> amenities</p>',
    },
  },
  collectionBindings: {
    'amenitieslistcommunity.items': {
      label: 'Community Amenities',
      collection: 'amenities',
      filter: { group: 'Community' },
    },
    'amenitieslistcommunity.photos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      defaultLimit: 8,
    },
  },
};

(AmenitiesListCommunity as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenitiesListCommunity;
